import { Tag, Dropdown, MenuProps } from "antd";
import { ColumnsType } from "antd/es/table";
import { getFormatted } from "../../../lib/PhoneFormatter";
import { EllipsisOutlined } from "@ant-design/icons";
import { ReactNode, useCallback, useMemo, MouseEvent } from "react";
import { useGetFullName } from "../../../lib/hooks/getFullName";
import { useSidebarHeader } from "../../../lib/hooks/sidebarHeader";
import { useGetUserRoleTagProps } from "../../../lib/hooks/getUserRoleTagProps";
import { useGetColumnSortValue } from "../../../lib/hooks/useGetColumnSortValue";
import { useDeleteUser } from "./useDeleteUser";
import { useCanModify } from "./useCanModify";
import { useCanAction } from "./useCanAction";

export const useGetTableColumns = (
  onOpenSideBar: (
    header: string | ReactNode,
    userId: string,
    isEdit?: boolean
  ) => void,
  onOpenBlockModal: (userId: string, isBlockValue: boolean) => void,
  openDeleteModal: (id: string, title: string) => void
) => {
  const { getFullName } = useGetFullName();

  const { isLoading, onDelete } = useDeleteUser(openDeleteModal);

  const { canModify } = useCanModify();

  const { canAction } = useCanAction();

  const { getColumnSortValue } = useGetColumnSortValue("userOrder");

  const { getStatus, getHeader } = useSidebarHeader();

  const { getRoleTagProps } = useGetUserRoleTagProps();

  const onDeleteClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (!isLoading) {
        e.stopPropagation();
        onDelete(e.currentTarget.id);
      }
    },
    [onDelete, isLoading]
  );

  const columns: ColumnsType<any> = useMemo(
    () => [
      {
        title: "Name",
        dataIndex: "firstName",
        render: (_: string, record: any) => {
          const header: ReactNode = getHeader(record);
          const fullName: string = getFullName(record);

          return (
            <div
              className="elipsis"
              title={fullName}
              onClick={() => onOpenSideBar(header, record.id)}
            >
              <span className="ant-btn-link">{fullName}</span>
            </div>
          );
        },
        sorter: true,
        sortOrder: (() => getColumnSortValue("0", "1"))(),
        width: "19%",
        ellipsis: true,
      },
      {
        title: "Username",
        dataIndex: "userName",
        render: (value: string) => value || "-",
        width: "19%",
        ellipsis: true,
      },
      {
        title: "Email",
        dataIndex: "email",
        width: "19%",
        ellipsis: true,
      },
      {
        title: "Phone",
        dataIndex: "phone",
        render: (value: string) => getFormatted(value).slice(2),
        width: "17%",
      },
      {
        title: "Role",
        dataIndex: "role",
        render: (value: string) =>
          value ? <Tag {...getRoleTagProps(value)}>{value}</Tag> : "-",
        sorter: true,
        sortOrder: (() => getColumnSortValue("2", "3"))(),
        width: "9%",
      },
      {
        title: "Status",
        dataIndex: "status",
        render: (value: number) => getStatus(value),
        sorter: true,
        sortOrder: (() => getColumnSortValue("4", "5"))(),
        width: "10%",
      },
      {
        title: "Action",
        render: (_: any, record: any) => {
          const header: ReactNode = getHeader(record);

          const { id, role: recordRole, status } = record;

          const items: MenuProps["items"] = [
            {
              key: "1",
              label: (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenSideBar(header, id);
                  }}
                >
                  View Profile
                </div>
              ),
            },
          ];

          canModify(recordRole) &&
            items.push({
              key: "2",
              label: (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenSideBar(header, id, true);
                  }}
                >
                  Edit Profile
                </div>
              ),
            });

          status !== 3 &&
            canAction(recordRole, id, "Block") &&
            items.push({
              key: "3",
              label: (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenBlockModal(id, status === 1);
                  }}
                >
                  {`${status === 1 ? "Block" : "Unblock"} Profile`}
                </div>
              ),
            });

          canAction(recordRole, id, "Delete") &&
            items.push({
              key: "4",
              label: (
                <div id={id} onClick={onDeleteClick}>
                  Delete User
                </div>
              ),
            });

          return (
            <Dropdown menu={{ items }} trigger={["click"]} disabled={isLoading}>
              <EllipsisOutlined onClick={(e) => e.stopPropagation()} />
            </Dropdown>
          );
        },
        width: "7%",
      },
    ],
    [
      isLoading,
      getColumnSortValue,
      getFullName,
      getHeader,
      getRoleTagProps,
      getStatus,
      onOpenBlockModal,
      onOpenSideBar,
      onDeleteClick,
      canModify,
      canAction,
    ]
  );

  return { columns, isLoading };
};
